import { ROW_GAP, RowSimpleTemplate } from '@setplex/arcana'
import { useStoreMap, useUnit } from 'effector-react'
import type { JSX } from 'react'
import { Fragment, memo, useCallback, useEffect, type ReactNode } from 'react'
import { useInView } from 'react-intersection-observer'
import type { IContinueWatchingCardFormat } from '~/entities/continue-watching'
import type { PartnerProductFormatted } from '~/entities/partner-product'
import type { UiTvShowBundleInfo } from '~/entities/shows/bundles/index.h'
import type { UiTvBundleInfo } from '~/entities/tv/bundles/index.h'
import {
  seeAllLiveEventsClick,
  seeAllPartnerProgramClick,
} from '~/widgets/DynamicModule/model/row'
import type {
  EventWithPrice,
  ShowWithPrice,
  TvWithPrice,
  UiVodBundleInfo,
  VodWithPrice,
} from '~/shared/interfaces'
import { $seeAllTitle } from '~/shared/ui/translations'
import {
  CWCard,
  CustomBundleCard,
  EventCard,
  PartnerCard,
  TvChannelCard,
  TvShowCard,
  VodCard,
} from '../cards'
import {
  getDataTestIdRow,
  getRowBackgroundGradient,
  getRowBackgroundStyle,
  getRowContentType,
  getRowPosterImage,
  getRowType,
} from '../lib'
import { dynamicModuleModel } from '~/widgets/DynamicModule'

type Props = { id: string }

export const Row = memo(Row_)

Row_.displayName = 'Row'
function Row_({ id }: Props): JSX.Element {
  const row = useStoreMap({
    store: dynamicModuleModel.$viewRows,
    keys: [id],
    fn: (loaded, [id]) => loaded.find((row) => row.id === id),
  })

  const [
    loadRow,
    seeAllTitle,
    seeAllLiveEventsClickEvent,
    seeAllPartnerProgramClickEvent,
  ] = useUnit([
    dynamicModuleModel.loadRow,
    $seeAllTitle,
    seeAllLiveEventsClick,
    seeAllPartnerProgramClick,
  ])

  const { ref, inView } = useInView()

  useEffect(() => {
    if (row && inView && (row.type === 'Empty' || row.type === 'Dirty')) {
      loadRow(row.appearance)
    }
  }, [inView, loadRow, row])

  const getRowCardTemplate = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (item: any, idx: number) => {
      switch (row?.appearance.contentType) {
        case 'VOD':
          return <VodCard row={row} item={item as VodWithPrice} idx={idx} />
        case 'LIVE_EVENT':
          return <EventCard row={row} item={item as EventWithPrice} idx={idx} />
        case 'MIXED':
          return <CWCard row={row} item={item as IContinueWatchingCardFormat} />
        case 'PARTNER_PRODUCT':
          return (
            <PartnerCard row={row} item={item as PartnerProductFormatted} />
          )
        case 'TV_CHANNEL':
          return (
            <TvChannelCard row={row} item={item as TvWithPrice} idx={idx} />
          )
        case 'TV_SHOW':
          return <TvShowCard row={row} item={item as ShowWithPrice} idx={idx} />
        case 'VOD_BUNDLE':
        case 'TV_SHOW_BUNDLE':
        case 'TV_BUNDLE':
          return (
            <CustomBundleCard
              row={row}
              item={
                item as UiVodBundleInfo | UiTvBundleInfo | UiTvShowBundleInfo
              }
            />
          )
        default:
          return <></>
      }
    },
    [row]
  )

  const rowContentType = getRowContentType(row)
  const rowBackgroundGradient = getRowBackgroundGradient(row)
  const rowBackgroundStyle = getRowBackgroundStyle(row)
  const posterImage = getRowPosterImage(row)
  const withDescriptionRow =
    row?.appearance.contentType === 'LIVE_EVENT' ||
    row?.appearance.contentType === 'MIXED' ||
    row?.appearance.contentType === 'PARTNER_PRODUCT'

  const handleOnSeeAllClick = () => {
    const contentType = row?.appearance.contentType

    if (contentType === 'LIVE_EVENT' || contentType === 'PARTNER_PRODUCT') {
      const seeAllClickConfig = {
        PARTNER_PRODUCT: seeAllPartnerProgramClickEvent,
        LIVE_EVENT: seeAllLiveEventsClickEvent,
      }

      seeAllClickConfig[contentType]()
    }
  }

  if (!row) {
    return <></>
  }

  const {
    appearance: {
      description,
      title,
      style: { posterSize, topContent },
    },
    content,
    isMoreThanOnePage,
  } = row

  const isRowLoading =
    row.type === 'Empty' || row.type === 'Loading' || row.type === 'Awaiting'

  const type = getRowType(row)
  const gap = topContent ? ROW_GAP.SIZE_20 : ROW_GAP.SIZE_10

  const RowTemplate = (children: ReactNode) => {
    const dataTestIdRow = getDataTestIdRow(row)

    return (
      <RowSimpleTemplate
        gap={gap}
        innerRef={ref}
        description={description}
        size={posterSize}
        type={type}
        title={title}
        contentType={rowContentType}
        itemCount={content.length}
        backgroundStyle={rowBackgroundStyle}
        backgroundGradient={rowBackgroundGradient}
        isMoreThanOnePage={isMoreThanOnePage}
        rowPosterImage={posterImage}
        onSeeAllClick={handleOnSeeAllClick}
        withCardDescription={withDescriptionRow}
        buttonTitle={seeAllTitle}
        isRowLoading={isRowLoading}
        dataTestIdRow={dataTestIdRow}
      >
        {children}
      </RowSimpleTemplate>
    )
  }

  if (isRowLoading) {
    return RowTemplate(<></>)
  }

  if (row.type === 'Failed' || row.content.length === 0) {
    return <></>
  }

  return RowTemplate(
    <>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {content.map((item: any, idx: number) => (
        <Fragment key={idx}>{getRowCardTemplate(item, idx)}</Fragment>
      ))}
    </>
  )
}
